import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, UserPlus, Key, CalendarDays, Heart, Upload, User, ShieldCheck, AlertCircle, MapPin, Route, X, Users, LogOut, QrCode, UserRound, Copy, Anchor, ChevronDown, ChevronRight } from "lucide-react";
import { api, fileUrl, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLiveEvents } from "@/lib/useLiveEvents";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import CabinArrangementSection from "./CabinArrangementSection";
import ItinerarySection from "./ItinerarySection";
import PastGuestsPicker from "./PastGuestsPicker";
import PortThumbnail from "@/components/PortThumbnail";
import QRCode from "react-qr-code";

const emptyTrip = { name: "", arrival: "", departure: "", active: true, default_embark_port: "", default_embark_at: "", default_disembark_port: "", default_disembark_at: "" };

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch { return iso; }
}
function toIsoDate(str) {
  if (!str) return "";
  // input type=date returns YYYY-MM-DD; convert to full ISO at midnight UTC
  if (str.length === 10) return `${str}T00:00:00+00:00`;
  return str;
}
function fromIsoToDateInput(iso) {
  if (!iso) return "";
  try { return new Date(iso).toISOString().slice(0, 10); } catch { return ""; }
}

export default function TripsPanel() {
  const { admin } = useAuth();
  // Photos flow to every dashboard/crew-management role. The only surfaces
  // that hide them are the public guest-device endpoints (iPads, journey
  // page), which never send image_path down in the first place.
  const canSeePhotos = !!admin?.role;
  const [trips, setTrips] = useState([]);
  // Foldable trip cards — by default only the ACTIVE trip stays open so
  // Managers land on the sailing roster; inactive/historic trips collapse
  // and the whole page fits without scroll-fatigue. Ids are stored in a
  // Set so toggling is O(1) and doesn't rebuild the whole list.
  const [expandedTripIds, setExpandedTripIds] = useState(() => new Set());
  const toggleTrip = (id) => setExpandedTripIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const [cabins, setCabins] = useState([]);
  const [tripOpen, setTripOpen] = useState(false);
  const [guestOpen, setGuestOpen] = useState(false);
  const [tripForm, setTripForm] = useState(emptyTrip);
  const [selectedTripId, setSelectedTripId] = useState(null);
  const [guestForm, setGuestForm] = useState({ cabin_id: "", name: "", pin: "", partner_guest_id: "", female: false, image_path: "", age: "", minor: false, guest_type: "guest", stays: [{ id: "s0", embark_at: "", embark_port: "", disembark_at: "", disembark_port: "" }] });
  const [editingGuest, setEditingGuest] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [pastPickerOpen, setPastPickerOpen] = useState(false);
  // QR access — a floating guest / day visitor doesn't have a cabin PIN,
  // so the crew hands out a signed short-URL. State holds the guest we're
  // minting for + the token/expiry from the backend.
  const [qrGuest, setQrGuest] = useState(null); // { guest, trip }
  const [qrData, setQrData] = useState(null); // { token, expires_at, url }
  const [qrLoading, setQrLoading] = useState(false);
  // Set of guest_ids that have submitted the wellness questionnaire — used
  // to render a small "Wellness ✓" / "Wellness —" chip beside each guest.
  const [healthSubmitted, setHealthSubmitted] = useState(() => new Set());

  const load = async () => {
    const [t, c, h] = await Promise.all([
      api.get("/admin/charters"),
      api.get("/admin/cabins"),
      api.get("/admin/guest-health-status").catch(() => ({ data: { submitted_guest_ids: [] } })),
    ]);
    setTrips(t.data || []);
    // First-load auto-expand: only the ACTIVE trip is open by default so
    // Managers land on the sailing roster without needing to scroll past
    // historic charters. Subsequent loads (WS-driven refresh) preserve
    // whatever the operator manually expanded.
    setExpandedTripIds((prev) => {
      if (prev.size > 0) return prev;
      const active = (t.data || []).filter((x) => x.active).map((x) => x.id);
      return new Set(active);
    });
    setCabins(c.data || []);
    setHealthSubmitted(new Set(h.data?.submitted_guest_ids || []));
  };
  useEffect(() => { load(); }, []);

  // Consume any cross-panel navigation intent stashed by the Cabins page
  // (see AdminDashboard `yspa:intent` listener). Runs whenever the trip &
  // cabin lists have been loaded so the target row / cabin is ready to
  // reference. Cleared after handling so revisiting the tab manually
  // doesn't retrigger the dialog.
  useEffect(() => {
    if (!trips.length || !cabins.length) return;
    const consume = () => {
      const intent = window.__yspaIntent;
      if (!intent) return;
      const targetTrip = intent.charter_id
        ? trips.find((t) => t.id === intent.charter_id)
        : trips.find((t) => t.active) || trips[0];
      if (!targetTrip) return;
      if (intent.kind === "add_guest_to_cabin") {
        setSelectedTripId(targetTrip.id);
        setEditingGuest(null);
        setGuestForm({
          cabin_id: intent.cabin_id || "",
          name: "", pin: "", partner_guest_id: "", female: false, image_path: "", age: "", minor: false, guest_type: "guest",
          stays: [{ id: `s${Date.now()}`, embark_at: "", embark_port: "", disembark_at: "", disembark_port: "" }],
        });
        setGuestOpen(true);
        toast.success(`Adding a guest to ${intent.cabin_name || "this cabin"}`);
      } else if (intent.kind === "highlight_guest") {
        // Scroll the guest's row into view and briefly ring it so the
        // operator sees where they landed. `data-guest-row` attribute is
        // set on the guest row div below.
        setTimeout(() => {
          const el = document.querySelector(`[data-guest-row="${intent.guest_id}"]`);
          if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "center" });
            el.classList.add("ring-2", "ring-accent", "ring-offset-2");
            setTimeout(() => el.classList.remove("ring-2", "ring-accent", "ring-offset-2"), 1800);
          }
        }, 200);
      } else if (intent.kind === "schedule_move_to_cabin") {
        // Scroll to the target trip's card, then re-fire the intent to the
        // nested CabinArrangementSection which listens on
        // `yspa:open-arrangement` — that component owns the arrangement
        // dialog and handles the actual roster + focus_cabin_id UI.
        setTimeout(() => {
          const el = document.querySelector(`[data-trip-card="${targetTrip.id}"]`);
          if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
          window.dispatchEvent(new CustomEvent("yspa:open-arrangement", { detail: {
            charter_id: targetTrip.id,
            focus_cabin_id: intent.cabin_id,
            focus_cabin_name: intent.cabin_name,
          }}));
        }, 250);
        toast.success(`Schedule a move into ${intent.cabin_name || "this cabin"}`);
      }
      window.__yspaIntent = null;
    };
    // Consume immediately (in case the intent was set before mount) and
    // whenever AdminDashboard re-fires the ready event.
    consume();
    window.addEventListener("yspa:intent-ready", consume);
    return () => window.removeEventListener("yspa:intent-ready", consume);
  }, [trips, cabins]);

  // Every open Trips panel across the fleet listens for `charter.updated`
  // events so a manager editing a guest sees the roster reflect on their
  // own screen even when the mutation came from a different browser.
  useLiveEvents((ev) => { if (ev?.type === "charter.updated") load(); });

  const saveTrip = async () => {
    try {
      // Normalise datetime-local → ISO for the trip defaults so the
      // backend stores a comparable string. Empty strings fall through
      // as null.
      const dtToIso = (v) => (v ? new Date(v).toISOString() : null);
      const payload = {
        name: tripForm.name,
        arrival: toIsoDate(tripForm.arrival),
        departure: toIsoDate(tripForm.departure),
        active: tripForm.active,
        default_embark_port: tripForm.default_embark_port || null,
        default_embark_at: dtToIso(tripForm.default_embark_at),
        default_disembark_port: tripForm.default_disembark_port || null,
        default_disembark_at: dtToIso(tripForm.default_disembark_at),
      };
      if (!payload.arrival || !payload.departure) { toast.error("Arrival and departure dates required"); return; }
      if (tripForm.id) await api.put(`/admin/charters/${tripForm.id}`, payload);
      else await api.post("/admin/charters", payload);
      toast.success("Trip saved");
      setTripOpen(false); load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Could not save trip"); }
  };

  const removeTrip = async (t) => {
    const nm = (t.name || "").trim();
    if (!nm) {
      // Legacy charters without a name still allowed via one plain confirm.
      if (!window.confirm(`Delete this untitled trip (${fmtDate(t.arrival)} → ${fmtDate(t.departure)})?`)) return;
      try {
        await api.delete(`/admin/charters/${t.id}`, { params: { confirm: "" } });
        load();
      } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Delete failed"); }
      return;
    }
    // Two-step confirmation: a plain warning, then the name must be retyped
    // verbatim. Prevents an accidental full-charter wipe from a slip of the
    // mouse. Backend enforces the same guard so any client (curl, other UI)
    // still can't sidestep it.
    if (!window.confirm(
      `Delete trip "${nm}"?\n\nEverything joined to the charter (guests, cabin arrangements, itinerary) will be permanently lost.\n\nYou'll be asked to retype the trip's name to confirm.`
    )) return;
    const typed = window.prompt(`To confirm, please type the trip name EXACTLY as shown:\n\n${nm}`);
    if (typed === null) return;
    if (typed.trim() !== nm) {
      toast.error("Delete cancelled — the name you typed does not match.");
      return;
    }
    try {
      await api.delete(`/admin/charters/${t.id}`, { params: { confirm: nm } });
      toast.success(`Trip "${nm}" deleted`);
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Delete failed"); }
  };

  const openNewGuest = (tripId) => {
    setSelectedTripId(tripId);
    setEditingGuest(null);
    // Prefill the first stay from the trip's declared defaults so the
    // crew doesn't retype the same port + time for every passenger.
    // `datetime-local` inputs need `YYYY-MM-DDTHH:MM` — slice ISO
    // strings to that shape when a default is present.
    const trip = trips.find((t) => t.id === tripId) || {};
    const defEA = trip.default_embark_at ? String(trip.default_embark_at).slice(0, 16) : "";
    const defDA = trip.default_disembark_at ? String(trip.default_disembark_at).slice(0, 16) : "";
    setGuestForm({ cabin_id: "", name: "", pin: "", partner_guest_id: "", female: false, image_path: "", age: "", minor: false, guest_type: "guest", is_primary: false,
                    stays: [{
                      id: `s${Date.now()}`,
                      embark_at: defEA,
                      embark_port: trip.default_embark_port || "",
                      disembark_at: defDA,
                      disembark_port: trip.default_disembark_port || "",
                    }] });
    setGuestOpen(true);
  };

  const openEditGuest = (tripId, g) => {
    setSelectedTripId(tripId);
    setEditingGuest(g);
    // Restore the guest's stay history if it exists; otherwise synthesise a
    // single segment from the legacy top-level embark/disembark fields so
    // the editor is never empty.
    const stays = (g.stays && g.stays.length > 0)
      ? g.stays.map((s, i) => ({
          id: s.id || `s${Date.now()}-${i}`,
          embark_at: (s.embark_at || "").slice(0, 16),
          embark_port: s.embark_port || "",
          disembark_at: (s.disembark_at || "").slice(0, 16),
          disembark_port: s.disembark_port || "",
        }))
      : [{
          id: `s${Date.now()}`,
          embark_at: (g.embark_at || "").slice(0, 16),
          embark_port: g.embark_port || "",
          disembark_at: (g.disembark_at || "").slice(0, 16),
          disembark_port: g.disembark_port || "",
        }];
    setGuestForm({ cabin_id: g.cabin_id || "", name: g.name, pin: g.pin || "", partner_guest_id: g.partner_guest_id || "", female: !!g.female, image_path: g.image_path || "", age: g.age ?? "", minor: !!g.minor, guest_type: g.guest_type || "guest", is_primary: !!g.is_primary,
                    stays });
    setGuestOpen(true);
  };

  // Legacy guests seeded before the id/guest_id merge sometimes have `id=null`.
  // Always fall back to `guest_id` so the URL never becomes "/guests/undefined".
  const gIdent = (g) => g?.id || g?.guest_id;

  const uploadGuestPhoto = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploadingPhoto(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setGuestForm((f) => ({ ...f, image_path: data.path }));
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Upload failed");
    } finally { setUploadingPhoto(false); }
  };

  const saveGuest = async () => {
    try {
      if (!guestForm.name.trim()) { toast.error("Name required"); return; }
      // Cabin is optional now — floating guests and day visitors don't
      // occupy a berth. Only enforce PIN rules when a cabin was picked
      // (day visitors authenticate via the QR-based /visit flow).
      if (guestForm.cabin_id && guestForm.pin && !/^\d{4}$/.test(guestForm.pin)) { toast.error("PIN must be 4 digits"); return; }
      // Reject overlapping / inverted stays so a bad drag from the datepicker
      // can't create a "negative" stay that would confuse the roster.
      const cleanStays = (guestForm.stays || []).map((s) => ({
        id: s.id,
        embark_at: s.embark_at || null,
        embark_port: s.embark_port || null,
        disembark_at: s.disembark_at || null,
        disembark_port: s.disembark_port || null,
      }));
      for (const s of cleanStays) {
        if (s.embark_at && s.disembark_at && s.disembark_at < s.embark_at) {
          toast.error("A stay's disembark date must be after its embark date"); return;
        }
      }
      const payload = { ...guestForm,
        cabin_id: guestForm.cabin_id || null,
        guest_type: guestForm.guest_type || "guest",
        is_primary: !!guestForm.is_primary,
        partner_guest_id: guestForm.partner_guest_id || null,
        image_path: guestForm.image_path || null,
        // Age comes off a numeric text input; coerce to int or drop to null
        // so Pydantic doesn't reject an empty string on the backend.
        // Minor rule: if the crew flagged the guest as under-18, drop any
        // adult age they may have typed by mistake — backend enforces the
        // same rule, this just keeps the two in agreement on the client.
        age: (() => {
          if (guestForm.age === "" || guestForm.age == null) return null;
          const n = parseInt(guestForm.age, 10);
          if (!Number.isFinite(n)) return null;
          if (guestForm.minor && n >= 18) return null;
          return n;
        })(),
        minor: !!guestForm.minor,
        stays: cleanStays,
        // Also send the top-level fields (backend recomputes these from the
        // first/last stay anyway, but this keeps older API consumers safe).
        embark_at: cleanStays[0]?.embark_at || null,
        embark_port: cleanStays[0]?.embark_port || null,
        disembark_at: cleanStays[cleanStays.length - 1]?.disembark_at || null,
        disembark_port: cleanStays[cleanStays.length - 1]?.disembark_port || null,
      };
      if (editingGuest) {
        await api.put(`/admin/charters/${selectedTripId}/guests/${gIdent(editingGuest)}`, payload);
      } else {
        await api.post(`/admin/charters/${selectedTripId}/guests`, payload);
      }
      toast.success(editingGuest ? "Guest updated" : "Guest added");
      setGuestOpen(false); load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Could not save guest"); }
  };

  const removeGuest = async (tripId, g) => {
    if (!window.confirm(`Remove ${g.name}?`)) return;
    await api.delete(`/admin/charters/${tripId}/guests/${gIdent(g)}`);
    load();
  };

  const disembarkNow = async (tripId, g) => {
    if (!window.confirm(`Mark ${g.name} as fully disembarked now?\n\nThis will stamp the current date/time as their disembark and remove them from the onboard count.`)) return;
    try {
      await api.post(`/admin/guests/${gIdent(g)}/disembark-now`, {});
      toast.success(`${g.name} disembarked`);
      load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Could not disembark");
    }
  };

  // Mint a QR access link for a floating guest / day visitor. TTL defaults
  // to "end of the ship-local day" for day visitors (per the product spec)
  // and 12 h for floating guests (they'll typically be allocated a cabin
  // within a few hours). The backend caps TTL at 24 h so a runaway custom
  // value stays safe.
  const openQrForGuest = async (trip, g) => {
    setQrGuest({ trip, guest: g });
    setQrData(null);
    setQrLoading(true);
    try {
      const isDayVisitor = (g.guest_type === "day_visitor");
      let ttl_minutes = 12 * 60; // floating default
      if (isDayVisitor) {
        // Minutes until the end of TODAY in the browser's local zone. The
        // yacht's crew iPad matches the ship's timezone in practice, so
        // "end of day" reads correctly.
        const now = new Date();
        const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0);
        ttl_minutes = Math.max(30, Math.round((midnight - now) / 60000));
      }
      const { data } = await api.post("/admin/guests/access-token", {
        charter_id: trip.id,
        guest_id: gIdent(g),
        ttl_minutes,
      });
      const url = `${window.location.origin}/visit/${data.token}`;
      setQrData({ token: data.token, expires_at: data.expires_at, url });
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Could not mint a QR code");
      setQrGuest(null);
    } finally {
      setQrLoading(false);
    }
  };

  const copyQrUrl = async () => {
    if (!qrData?.url) return;
    try {
      await navigator.clipboard.writeText(qrData.url);
      toast.success("Link copied");
    } catch {
      toast.error("Could not copy — long-press the URL below to select it");
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="font-display text-3xl">Trips & guests</h2>
          <p className="text-sm text-muted-foreground mt-1">Charter timeframes + guest cabin assignments with 4-digit PINs.</p>
        </div>
        <Button onClick={() => { setTripForm(emptyTrip); setTripOpen(true); }} className="rounded-full bg-primary hover:bg-primary/90" data-testid="new-trip-btn">
          <Plus className="h-4 w-4 mr-2" /> New trip
        </Button>
      </div>

      {trips.length === 0 && cabins.length === 0 && (
        <div className="border border-dashed border-border p-8 mb-6 text-sm text-muted-foreground">
          Start by adding cabins in the <span className="text-primary font-medium">Cabins</span> tab, then create a trip here.
        </div>
      )}

      <div className="space-y-6" data-testid="trips-list">
        {trips.map((t) => {
          const isExpanded = expandedTripIds.has(t.id);
          return (
          <div key={t.id} data-trip-card={t.id} className="bg-card border border-border" data-testid={`trip-${t.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-4 p-5 border-b border-border">
              <button
                onClick={() => toggleTrip(t.id)}
                className="flex items-start gap-3 text-left flex-1 min-w-0 group"
                data-testid={`trip-fold-${t.id}`}
                aria-expanded={isExpanded}
              >
                <span className="mt-2 shrink-0 text-muted-foreground group-hover:text-primary transition-colors">
                  {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </span>
                <div className="min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h3 className="font-display text-2xl">{t.name || "Charter"}</h3>
                    <Badge variant={t.active ? "default" : "outline"} className={t.active ? "bg-primary" : ""}>{t.active ? "Active" : "Inactive"}</Badge>
                    {!isExpanded && (t.guests || []).length > 0 && (
                      <span className="text-xs text-muted-foreground">
                        · {(t.guests || []).length} guest{(t.guests || []).length === 1 ? "" : "s"}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mt-2">
                    <CalendarDays className="h-3.5 w-3.5" />
                    <span>{fmtDate(t.arrival)} → {fmtDate(t.departure)}</span>
                  </div>
                </div>
              </button>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => openNewGuest(t.id)} data-testid={`add-guest-${t.id}`}>
                  <UserPlus className="h-3 w-3 mr-1" /> Add guest
                </Button>
                <Button size="sm" variant="outline" onClick={() => { setTripForm({ ...t, arrival: fromIsoToDateInput(t.arrival), departure: fromIsoToDateInput(t.departure) }); setTripOpen(true); }} data-testid={`edit-trip-${t.id}`}>
                  <Pencil className="h-3 w-3" />
                </Button>
                <Button size="sm" variant="outline" onClick={() => removeTrip(t)} className="text-destructive border-destructive/40" data-testid={`delete-trip-${t.id}`}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </div>

            {isExpanded && ((t.guests || []).length > 0 ? (
              <ul className="divide-y divide-border">
                {(t.guests || []).map((g) => {
                  const partner = (t.guests || []).find((x) => x.id === g.partner_guest_id);
                  return (
                    <li key={g.id} className="p-5 flex flex-wrap items-center justify-between gap-4 rounded transition-shadow" data-guest-row={gIdent(g)} data-testid={`trip-guest-${g.id}`}>
                      <div className="flex items-center gap-4">
                        {canSeePhotos && (g.image_path ? (
                          <img
                            src={fileUrl(g.image_path)}
                            alt={g.name}
                            className="w-12 h-12 rounded-full object-cover border border-border"
                            data-testid={`trip-guest-avatar-${g.id}`}
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-full bg-muted border border-border flex items-center justify-center">
                            <User className="h-5 w-5 text-muted-foreground" />
                          </div>
                        ))}
                        <div>
                          <div className="font-display text-lg flex items-center gap-2 flex-wrap">
                            {g.name}
                            {partner && <Badge className="text-[10px] bg-accent/20 text-accent border-0 hover:bg-accent/20"><Heart className="h-2.5 w-2.5 mr-1" />{partner.name}</Badge>}
                            {g.is_primary && (
                              <Badge
                                className="text-[10px] bg-amber-500/20 text-amber-700 dark:text-amber-400 border border-amber-500/50 hover:bg-amber-500/20 font-medium"
                                data-testid={`guest-primary-badge-${g.id}`}
                                title="Primary guest — can priority-claim slots when the service is on in Settings"
                              >
                                <Key className="h-2.5 w-2.5 mr-1" strokeWidth={2.2} /> Primary
                              </Badge>
                            )}
                            {g.guest_type === "day_visitor" && (
                              <Badge
                                className="text-[10px] bg-sky-500/15 text-sky-700 dark:text-sky-400 border border-sky-500/40 hover:bg-sky-500/15"
                                data-testid={`guest-day-visitor-badge-${g.id}`}
                                title="Day visitor · no cabin · QR access expires at end of day"
                              >
                                <UserRound className="h-2.5 w-2.5 mr-1" /> Day visitor
                              </Badge>
                            )}
                            {(!g.cabin_id) && g.guest_type !== "day_visitor" && (
                              <Badge
                                className="text-[10px] bg-violet-500/15 text-violet-700 dark:text-violet-400 border border-violet-500/40 hover:bg-violet-500/15"
                                data-testid={`guest-floating-badge-${g.id}`}
                                title="No cabin allocated yet · QR access provided"
                              >
                                <Anchor className="h-2.5 w-2.5 mr-1" /> Floating
                              </Badge>
                            )}
                            {g.onboard === false && !g.disembark_at && (
                              <Badge
                                className="text-[10px] bg-amber-500/15 text-amber-700 dark:text-amber-400 border border-amber-500/40 hover:bg-amber-500/15"
                                data-testid={`guest-ashore-badge-${g.id}`}
                                title="Currently ashore (temporary — no disembark date recorded)"
                              >
                                Ashore
                              </Badge>
                            )}
                            {g.disembark_at && new Date(g.disembark_at) <= new Date() && (
                              <Badge
                                className="text-[10px] bg-rose-500/15 text-rose-700 dark:text-rose-400 border border-rose-500/40 hover:bg-rose-500/15"
                                data-testid={`guest-disembarked-badge-${g.id}`}
                                title="Disembarked"
                              >
                                Disembarked
                              </Badge>
                            )}
                            {healthSubmitted.has(g.guest_id || g.id) ? (
                              <Badge
                                className="text-[10px] bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/15"
                                data-testid={`guest-health-badge-yes-${g.id}`}
                                title="Wellness questionnaire submitted"
                              >
                                <ShieldCheck className="h-2.5 w-2.5 mr-1" /> Wellness ✓
                              </Badge>
                            ) : (
                              <Badge
                                variant="outline"
                                className="text-[10px] border-amber-500/40 text-amber-700 dark:text-amber-400"
                                data-testid={`guest-health-badge-no-${g.id}`}
                                title="Wellness questionnaire not yet submitted"
                              >
                                <AlertCircle className="h-2.5 w-2.5 mr-1" /> Wellness pending
                              </Badge>
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                            <span>{g.cabin_id ? <>Cabin · <span className="text-foreground/80">{g.cabin_name}</span></> : <span className="italic text-primary/60">No cabin allocated</span>}</span>
                            {(g.embark_at || g.disembark_at || g.embark_port || g.disembark_port) && (
                              <span className="inline-flex items-center gap-2 border-l border-border pl-2" data-testid={`guest-journey-${g.id}`}>
                                {(g.embark_port || g.embark_at) && (
                                  <span className="inline-flex items-center gap-1.5" title={`Embark${g.embark_port ? " · " + g.embark_port : ""}${g.embark_at ? " · " + fmtDate(g.embark_at) : ""}`}>
                                    <PortThumbnail name={g.embark_port || ""} size={22} testId={`guest-embark-thumb-${g.id}`} />
                                    <span>
                                      {g.embark_at && <>Emb {fmtDate(g.embark_at)}</>}
                                      {g.embark_at && g.embark_port && " · "}
                                      {g.embark_port}
                                    </span>
                                  </span>
                                )}
                                {(g.embark_at || g.embark_port) && (g.disembark_at || g.disembark_port) && <span className="text-primary/30">→</span>}
                                {(g.disembark_port || g.disembark_at) && (
                                  <span className="inline-flex items-center gap-1.5" title={`Disembark${g.disembark_port ? " · " + g.disembark_port : ""}${g.disembark_at ? " · " + fmtDate(g.disembark_at) : ""}`}>
                                    <PortThumbnail name={g.disembark_port || ""} size={22} testId={`guest-disembark-thumb-${g.id}`} />
                                    <span>
                                      {g.disembark_at && <>Dis {fmtDate(g.disembark_at)}</>}
                                      {g.disembark_at && g.disembark_port && " · "}
                                      {g.disembark_port}
                                    </span>
                                  </span>
                                )}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        {g.pin ? (
                          <div className="inline-flex items-center gap-2 bg-muted px-4 py-2 border border-border">
                            <Key className="h-3 w-3 text-accent" />
                            <span className="font-mono text-lg tracking-widest" data-testid={`pin-${g.id}`}>{g.pin}</span>
                          </div>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openQrForGuest(t, g)}
                            className="text-sky-700 border-sky-500/40 hover:bg-sky-500/10 dark:text-sky-400"
                            data-testid={`qr-guest-${g.id}`}
                            title="Show QR access link"
                          >
                            <QrCode className="h-3.5 w-3.5 mr-1.5" /> QR
                          </Button>
                        )}
                        <Button size="sm" variant="outline" onClick={() => openEditGuest(t.id, g)} data-testid={`edit-guest-${g.id}`}><Pencil className="h-3 w-3" /></Button>
                        {(!g.disembark_at || new Date(g.disembark_at) > new Date()) && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => disembarkNow(t.id, g)}
                            className="text-rose-700 border-rose-500/40 hover:bg-rose-500/10 dark:text-rose-400"
                            title="Mark as fully disembarked now"
                            data-testid={`disembark-guest-${g.id}`}
                          >
                            <LogOut className="h-3 w-3" />
                          </Button>
                        )}
                        <Button size="sm" variant="outline" onClick={() => removeGuest(t.id, g)} className="text-destructive border-destructive/40" data-testid={`delete-guest-${g.id}`}><Trash2 className="h-3 w-3" /></Button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div className="p-5 text-sm text-muted-foreground">No guests yet. Click "Add guest" to assign a cabin.</div>
            ))}
            {isExpanded && <CabinArrangementSection charter={t} cabins={cabins} onChanged={load} />}
            {isExpanded && <ItinerarySection charter={t} />}
          </div>
          );
        })}
      </div>

      {/* Trip Dialog */}
      <Dialog open={tripOpen} onOpenChange={setTripOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="trip-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">{tripForm.id ? "Edit" : "New"} trip</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label className="overline text-primary/60">Trip name (optional)</Label>
              <Input value={tripForm.name} onChange={(e) => setTripForm({ ...tripForm, name: e.target.value })} placeholder="e.g. Summer Cruise 2026" data-testid="tf-trip-name" className="mt-2" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="overline text-primary/60">Arrival</Label>
                <Input type="date" value={tripForm.arrival} onChange={(e) => setTripForm({ ...tripForm, arrival: e.target.value })} data-testid="tf-arrival" className="mt-2" /></div>
              <div><Label className="overline text-primary/60">Departure</Label>
                <Input type="date" value={tripForm.departure} onChange={(e) => setTripForm({ ...tripForm, departure: e.target.value })} data-testid="tf-departure" className="mt-2" /></div>
            </div>

            {/* Trip-level embark / disembark defaults — pre-fills every new
                guest's first stay row so the crew doesn't retype the same
                port + time for each passenger. Guests can still override
                on their own row. */}
            <div className="border-t border-border pt-4 mt-2 space-y-3">
              <div className="overline text-primary/60 text-[10px]">Default embark / disembark for new guests</div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="overline text-primary/60 text-[10px]">Embark port</Label>
                  <Input
                    value={tripForm.default_embark_port || ""}
                    onChange={(e) => setTripForm({ ...tripForm, default_embark_port: e.target.value })}
                    placeholder="e.g. Monaco"
                    data-testid="tf-default-embark-port"
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label className="overline text-primary/60 text-[10px]">Embark date & time</Label>
                  <Input
                    type="datetime-local"
                    value={tripForm.default_embark_at || ""}
                    onChange={(e) => setTripForm({ ...tripForm, default_embark_at: e.target.value })}
                    data-testid="tf-default-embark-at"
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label className="overline text-primary/60 text-[10px]">Disembark port</Label>
                  <Input
                    value={tripForm.default_disembark_port || ""}
                    onChange={(e) => setTripForm({ ...tripForm, default_disembark_port: e.target.value })}
                    placeholder="e.g. Palma"
                    data-testid="tf-default-disembark-port"
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label className="overline text-primary/60 text-[10px]">Disembark date & time</Label>
                  <Input
                    type="datetime-local"
                    value={tripForm.default_disembark_at || ""}
                    onChange={(e) => setTripForm({ ...tripForm, default_disembark_at: e.target.value })}
                    data-testid="tf-default-disembark-at"
                    className="mt-2"
                  />
                </div>
              </div>
              <p className="text-[11px] text-muted-foreground italic">
                Optional. When set, adding a new guest to this trip pre-fills their first stay from these values.
                Each guest can still override their own dates or ports.
              </p>
            </div>

            <div className="flex items-center gap-3">
              <Switch checked={tripForm.active} onCheckedChange={(v) => setTripForm({ ...tripForm, active: v })} data-testid="tf-active" />
              <Label>Active (guests can log in with their PIN)</Label>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setTripOpen(false)}>Cancel</Button>
              <Button onClick={saveTrip} className="bg-primary hover:bg-primary/90" data-testid="tf-save">Save trip</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Guest Dialog */}
      <Dialog open={guestOpen} onOpenChange={setGuestOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="guest-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editingGuest ? "Edit" : "Add"} guest</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="overline text-primary/60">Guest kind</Label>
              <div className="mt-2 grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setGuestForm({ ...guestForm, guest_type: "guest" })}
                  className={`p-3 border text-left transition-all ${guestForm.guest_type !== "day_visitor" ? "border-accent bg-accent/5 ring-1 ring-accent/40" : "border-border hover:border-primary/40"}`}
                  data-testid="gf-kind-guest"
                >
                  <div className="flex items-center gap-2 font-medium text-sm">
                    <User className="h-3.5 w-3.5" /> Staying guest
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-1 leading-snug">Sleeps aboard. Optional cabin — leave empty to allocate later ("floating guest").</div>
                </button>
                <button
                  type="button"
                  onClick={() => setGuestForm({ ...guestForm, guest_type: "day_visitor", cabin_id: "" })}
                  className={`p-3 border text-left transition-all ${guestForm.guest_type === "day_visitor" ? "border-sky-500 bg-sky-500/5 ring-1 ring-sky-500/40" : "border-border hover:border-primary/40"}`}
                  data-testid="gf-kind-day-visitor"
                >
                  <div className="flex items-center gap-2 font-medium text-sm text-sky-700 dark:text-sky-400">
                    <UserRound className="h-3.5 w-3.5" /> Day visitor
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-1 leading-snug">Visits for a day. No cabin, no PIN — access via QR link that expires at end of day.</div>
                </button>
              </div>
            </div>
            <div>
              <Label className="overline text-primary/60">Cabin {guestForm.guest_type === "day_visitor" && <span className="normal-case tracking-normal text-muted-foreground text-[10px]">(day visitors don't take a cabin)</span>}</Label>
              <Select
                value={guestForm.cabin_id || "none"}
                onValueChange={(v) => setGuestForm({ ...guestForm, cabin_id: v === "none" ? "" : v })}
                disabled={guestForm.guest_type === "day_visitor"}
              >
                <SelectTrigger className="mt-2" data-testid="gf-cabin"><SelectValue placeholder="Select cabin" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">
                    <span className="text-primary/60 italic">— None (allocate later · floating guest) —</span>
                  </SelectItem>
                  {cabins.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {!guestForm.cabin_id && guestForm.guest_type !== "day_visitor" && (
                <p className="text-[11px] text-violet-700 dark:text-violet-400 mt-1.5 flex items-center gap-1.5">
                  <Anchor className="h-3 w-3" /> This will be saved as a floating guest — no PIN yet. Assign a cabin later to activate PIN login, or share the QR access link.
                </p>
              )}
            </div>
            <div>
              <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
                <div className="flex items-center gap-3 flex-wrap">
                  <Label className="overline text-primary/60">Guest name</Label>
                  <label
                    htmlFor="gf-is-primary"
                    className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border cursor-pointer text-[10px] uppercase tracking-widest transition-colors ${
                      guestForm.is_primary
                        ? "border-amber-500 bg-amber-500/15 text-amber-700 dark:text-amber-400"
                        : "border-border text-muted-foreground hover:border-amber-500/50 hover:text-amber-700"
                    }`}
                    title="Primary guest — can priority-claim slots when the Priority-Claim service is on in Settings"
                    data-testid="gf-is-primary-chip"
                  >
                    <input
                      id="gf-is-primary"
                      type="checkbox"
                      checked={!!guestForm.is_primary}
                      onChange={(e) => setGuestForm({ ...guestForm, is_primary: e.target.checked })}
                      className="h-3 w-3 accent-amber-500"
                      data-testid="gf-is-primary"
                    />
                    <Key className="h-2.5 w-2.5" strokeWidth={2.2} />
                    Primary
                  </label>
                </div>
                {!editingGuest && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setPastPickerOpen(true)}
                    className="text-xs h-7 rounded-full border-accent/40 text-accent hover:bg-accent/5"
                    data-testid="gf-past-guests-btn"
                    title="Reuse a returning guest's saved profile"
                  >
                    <Users className="h-3 w-3 mr-1" /> Load from past guests
                  </Button>
                )}
              </div>
              <Input value={guestForm.name} onChange={(e) => setGuestForm({ ...guestForm, name: e.target.value })} data-testid="gf-name" className="mt-2" />
              {guestForm.is_primary && (
                <p className="text-[11px] text-amber-700 dark:text-amber-400/80 mt-1.5 leading-snug" data-testid="gf-is-primary-hint">
                  Primary guest — pre-empts regular bookings when the Priority-Claim service is on in Settings. Bumped guests receive a courteous notice.
                </p>
              )}
            </div>
            <div>
              <Label className="overline text-primary/60">Guest photo <span className="normal-case tracking-normal text-muted-foreground text-[10px]">(internal · crew view only)</span></Label>
              <div className="mt-2 flex items-center gap-4" data-testid="gf-photo-block">
                {guestForm.image_path ? (
                  <img src={fileUrl(guestForm.image_path)} alt="" className="w-20 h-20 rounded-full object-cover border border-border" data-testid="gf-photo-preview" />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-muted border border-border flex items-center justify-center">
                    <User className="h-8 w-8 text-muted-foreground" />
                  </div>
                )}
                <div className="flex flex-col gap-2">
                  <label className="inline-flex items-center gap-2 px-3 py-2 border border-border hover:border-primary rounded cursor-pointer text-xs uppercase tracking-widest" data-testid="gf-photo-upload">
                    <Upload className="h-3 w-3" />
                    {uploadingPhoto ? "Uploading…" : guestForm.image_path ? "Replace photo" : "Upload photo"}
                    <input type="file" accept="image/*" onChange={uploadGuestPhoto} className="hidden" disabled={uploadingPhoto} />
                  </label>
                  {guestForm.image_path && (
                    <Button size="sm" variant="outline" onClick={() => setGuestForm({ ...guestForm, image_path: "" })} className="text-destructive border-destructive/40 text-xs" data-testid="gf-photo-remove">
                      Remove
                    </Button>
                  )}
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">Only Admin, Manager, Supervisor and Crew see this photo on the roster & booking screens. Guests and iPads never see it.</p>
            </div>
            <div className="flex items-center gap-3">
              <input
                id="gf-female"
                type="checkbox"
                checked={guestForm.female}
                onChange={(e) => setGuestForm({ ...guestForm, female: e.target.checked })}
                className="h-4 w-4 accent-accent"
                data-testid="gf-female"
              />
              <label htmlFor="gf-female" className="text-sm">Female (shows female-only treatment questions)</label>
            </div>

            <div>
              <Label className="overline text-primary/60">PIN (4 digits · leave empty to auto-generate) {(!guestForm.cabin_id || guestForm.guest_type === "day_visitor") && <span className="normal-case tracking-normal text-muted-foreground text-[10px]">(PIN requires a cabin)</span>}</Label>
              <Input
                value={guestForm.pin}
                onChange={(e) => setGuestForm({ ...guestForm, pin: e.target.value.replace(/\D/g, "").slice(0, 4) })}
                inputMode="numeric" maxLength={4} placeholder="1234"
                data-testid="gf-pin"
                disabled={!guestForm.cabin_id || guestForm.guest_type === "day_visitor"}
                className="mt-2 font-mono tracking-widest text-lg"
              />
            </div>
            <div>
              <Label className="overline text-primary/60">Age (optional)</Label>
              <p className="text-xs text-muted-foreground mt-1">
                Shown to crew on the "Guests onboard" panel — useful context for elderly guests or children.
              </p>
              <Input
                type="number"
                min={0}
                max={120}
                value={guestForm.age}
                onChange={(e) => setGuestForm({ ...guestForm, age: e.target.value.replace(/[^\d]/g, "").slice(0, 3) })}
                placeholder="e.g. 42"
                inputMode="numeric"
                data-testid="gf-age"
                className="mt-2 w-32"
              />
              <label className="mt-3 flex items-start gap-3 border border-border bg-muted/30 p-3 cursor-pointer" data-testid="gf-minor-row">
                <input
                  type="checkbox"
                  checked={!!guestForm.minor}
                  onChange={(e) => setGuestForm({ ...guestForm, minor: e.target.checked })}
                  className="mt-1 h-4 w-4 accent-primary"
                  data-testid="gf-minor"
                />
                <span className="text-sm">
                  <span className="font-medium">Minor (under 18)</span>
                  <span className="block text-[11px] text-muted-foreground mt-0.5">
                    Ticking this activates the 18+ adults-only gate even when the age box is empty. If an adult age (18+) was typed by mistake, it will be silently erased on save.
                  </span>
                </span>
              </label>
            </div>
            <div className="border-t border-border pt-4">
              <div className="flex items-center justify-between mb-2">
                <Label className="overline text-primary/60 flex items-center gap-2"><Route className="h-3 w-3" /> Embark & disembark periods</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setGuestForm((f) => ({
                    ...f,
                    stays: [...(f.stays || []), { id: `s${Date.now()}`, embark_at: "", embark_port: "", disembark_at: "", disembark_port: "" }],
                  }))}
                  className="text-xs h-7 rounded-full"
                  data-testid="gf-stay-add"
                >
                  <Plus className="h-3 w-3 mr-1" /> Add another period
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mb-3">
                A guest may leave and rejoin the yacht mid-trip. Each period is a distinct embark→disembark window; the cabin frees automatically after each disembark, and the guest returns automatically when the next embark date arrives.
              </p>
              <div className="space-y-3">
                {(guestForm.stays || []).map((stay, idx) => (
                  <div key={stay.id} className="p-3 border border-border bg-background/60 relative" data-testid={`gf-stay-${idx}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[10px] uppercase tracking-widest text-primary/60">Period {idx + 1}</span>
                      {(guestForm.stays.length > 1) && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setGuestForm((f) => ({ ...f, stays: f.stays.filter((_, i) => i !== idx) }))}
                          className="h-6 w-6 p-0 text-destructive hover:bg-destructive/10"
                          aria-label={`Remove period ${idx + 1}`}
                          data-testid={`gf-stay-remove-${idx}`}
                        >
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-[10px] uppercase tracking-widest text-primary/50">Embark date &amp; time</Label>
                        <Input
                          type="datetime-local"
                          value={stay.embark_at || ""}
                          onChange={(e) => setGuestForm((f) => ({
                            ...f,
                            stays: f.stays.map((s, i) => i === idx ? { ...s, embark_at: e.target.value } : s),
                          }))}
                          className="mt-1.5"
                          data-testid={`gf-stay-${idx}-embark-at`}
                        />
                      </div>
                      <div>
                        <Label className="text-[10px] uppercase tracking-widest text-primary/50">Embark port</Label>
                        <div className="mt-1.5 flex items-center gap-2">
                          {stay.embark_port ? (
                            <PortThumbnail name={stay.embark_port} size={38} testId={`gf-stay-${idx}-embark-thumb`} />
                          ) : (
                            <div className="rounded-full border border-dashed border-border/60 bg-muted/40 flex items-center justify-center shrink-0" style={{ width: 38, height: 38 }} aria-hidden>
                              <MapPin className="h-3.5 w-3.5 text-primary/40" />
                            </div>
                          )}
                          <Input
                            value={stay.embark_port || ""}
                            onChange={(e) => setGuestForm((f) => ({
                              ...f,
                              stays: f.stays.map((s, i) => i === idx ? { ...s, embark_port: e.target.value } : s),
                            }))}
                            placeholder="e.g. Monaco"
                            className="flex-1"
                            data-testid={`gf-stay-${idx}-embark-port`}
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-[10px] uppercase tracking-widest text-primary/50">Disembark date &amp; time</Label>
                        <Input
                          type="datetime-local"
                          value={stay.disembark_at || ""}
                          onChange={(e) => setGuestForm((f) => ({
                            ...f,
                            stays: f.stays.map((s, i) => i === idx ? { ...s, disembark_at: e.target.value } : s),
                          }))}
                          className="mt-1.5"
                          data-testid={`gf-stay-${idx}-disembark-at`}
                        />
                      </div>
                      <div>
                        <Label className="text-[10px] uppercase tracking-widest text-primary/50">Disembark port</Label>
                        <div className="mt-1.5 flex items-center gap-2">
                          {stay.disembark_port ? (
                            <PortThumbnail name={stay.disembark_port} size={38} testId={`gf-stay-${idx}-disembark-thumb`} />
                          ) : (
                            <div className="rounded-full border border-dashed border-border/60 bg-muted/40 flex items-center justify-center shrink-0" style={{ width: 38, height: 38 }} aria-hidden>
                              <MapPin className="h-3.5 w-3.5 text-primary/40" />
                            </div>
                          )}
                          <Input
                            value={stay.disembark_port || ""}
                            onChange={(e) => setGuestForm((f) => ({
                              ...f,
                              stays: f.stays.map((s, i) => i === idx ? { ...s, disembark_port: e.target.value } : s),
                            }))}
                            placeholder="e.g. Nice"
                            className="flex-1"
                            data-testid={`gf-stay-${idx}-disembark-port`}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {selectedTripId && (
              <div>
                <Label className="overline text-primary/60">Couple partner (optional)</Label>
                <Select
                  value={guestForm.partner_guest_id || "none"}
                  onValueChange={(v) => setGuestForm({ ...guestForm, partner_guest_id: v === "none" ? "" : v })}
                >
                  <SelectTrigger className="mt-2" data-testid="gf-partner"><SelectValue placeholder="No partner" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— none —</SelectItem>
                    {(trips.find((t) => t.id === selectedTripId)?.guests || [])
                      .filter((g) => !editingGuest || gIdent(g) !== gIdent(editingGuest))
                      .map((g) => <SelectItem key={gIdent(g)} value={gIdent(g)}>{g.name} · {g.cabin_name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">Link this guest to their partner for couples treatments.</p>
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setGuestOpen(false)}>Cancel</Button>
              <Button onClick={saveGuest} className="bg-primary hover:bg-primary/90" data-testid="gf-save">{editingGuest ? "Save" : "Add"}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Nested past-guests picker — opens above the guest dialog and, on
          select, refills the name / photo / PIN / age / female flag while
          leaving cabin & stays untouched (those are per-trip). */}
      <PastGuestsPicker
        open={pastPickerOpen}
        onOpenChange={setPastPickerOpen}
        onPick={(p) => setGuestForm((f) => ({
          ...f,
          name: p.name || f.name,
          image_path: p.image_path || f.image_path,
          pin: p.pin || f.pin,
          age: p.age != null ? String(p.age) : f.age,
          female: !!p.female,
          // Stamp the roster link so the backend hydrates any missing
          // profile fields AND records `roster_id` on the guest doc —
          // that's what lets future bookings pull medical / preference
          // data from the roster instead of asking the guest anything.
          roster_member_id: p.roster_id || null,
        }))}
      />

      {/* QR access dialog — for floating guests and day visitors. Shows
          the signed short-URL as a scannable QR + a copy-link fallback. */}
      <Dialog open={!!qrGuest} onOpenChange={(o) => { if (!o) { setQrGuest(null); setQrData(null); } }}>
        <DialogContent className="max-w-md" data-testid="guest-qr-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <QrCode className="h-5 w-5 text-accent" />
              {qrGuest?.guest?.guest_type === "day_visitor" ? "Day visitor access" : "Guest access link"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-sm">
              <div className="font-medium">{qrGuest?.guest?.name}</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {qrGuest?.guest?.guest_type === "day_visitor" ? "Day visitor — full menu access, expires at end of day." : "Floating guest — no cabin yet, use this QR until they get one."}
              </div>
            </div>
            {qrLoading && <div className="text-sm text-muted-foreground py-6 text-center">Minting access link…</div>}
            {qrData && (
              <>
                <div className="flex items-center justify-center bg-white p-6 border border-border" data-testid="guest-qr-image">
                  <QRCode value={qrData.url} size={220} level="M" style={{ height: 220, width: 220 }} />
                </div>
                <div className="space-y-2">
                  <Label className="overline text-primary/60">Link</Label>
                  <div className="flex items-center gap-2">
                    <Input readOnly value={qrData.url} data-testid="guest-qr-url" className="text-xs font-mono" onFocus={(e) => e.target.select()} />
                    <Button size="sm" variant="outline" onClick={copyQrUrl} data-testid="guest-qr-copy" title="Copy link">
                      <Copy className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    Expires {qrData.expires_at ? new Date(qrData.expires_at).toLocaleString(undefined, { hour: "2-digit", minute: "2-digit", month: "short", day: "numeric" }) : "soon"}. Anyone with this link is signed in as {qrGuest?.guest?.name}.
                  </div>
                </div>
              </>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => { setQrGuest(null); setQrData(null); }} data-testid="guest-qr-close">Close</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
